import { useEffect, useState } from "react";
import { Radio, Users, Calendar } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const WebinarCard = ({ w }) => (
  <div data-testid={`webinar-card-${w.id}`} className="rounded-2xl overflow-hidden bg-[#0d0d0d] border border-white/5 text-left gold-glow-hover flex flex-col">
    <div className="relative h-44">
      <img src={w.image} alt={w.title} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
      {w.status === "live" ? (
        <span className="absolute top-3 left-3 flex items-center gap-1.5 bg-red-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
          <span className="w-1.5 h-1.5 rounded-full bg-white live-dot" /> Live
        </span>
      ) : (
        <span className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/70 border border-white/20 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
          <Calendar size={10} /> Upcoming
        </span>
      )}
    </div>
    <div className="p-5 flex flex-col flex-1">
      <h3 className="font-heading font-semibold text-white text-base">{w.title}</h3>
      <p className="mt-1 text-xs text-[#F5B301]">{w.host}</p>
      <p className="mt-2 text-xs text-zinc-500 leading-relaxed flex-1">{w.description}</p>
      <div className="mt-4 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs text-zinc-400">
          <Users size={13} /> {w.attendees.toLocaleString()} {w.status === "live" ? "watching" : "registered"}
        </span>
        <span className="text-xs text-zinc-500">{w.starts_at}</span>
      </div>
      <button
        data-testid={`webinar-join-btn-${w.id}`}
        onClick={() => toast.success(w.status === "live" ? `Joining "${w.title}"… (demo)` : `Reminder set for "${w.title}"!`)}
        className={`mt-4 w-full py-2.5 rounded-full text-xs font-bold transition-colors ${
          w.status === "live" ? "bg-[#F5B301] text-black hover:bg-[#D99E01]" : "border border-[#F5B301]/50 text-[#F5B301] hover:bg-[#F5B301]/10"
        }`}
      >
        {w.status === "live" ? "Join Live Now" : "Set Reminder"}
      </button>
    </div>
  </div>
);

export default function Live() {
  const [webinars, setWebinars] = useState([]);
  useEffect(() => { api.get("/webinars").then((r) => setWebinars(r.data)); }, []);
  const live = webinars.filter((w) => w.status === "live");
  const upcoming = webinars.filter((w) => w.status !== "live");

  return (
    <main className="relative pt-28 pb-24 max-w-7xl mx-auto px-4 sm:px-6">
      <div className="noise-overlay" />
      <div className="text-left mb-12">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight flex items-center gap-4">
          <span className="gold-gradient-text">Live</span> Webinars
          <Radio size={30} className="text-red-500 live-dot" />
        </h1>
        <p className="mt-3 text-zinc-500 text-sm md:text-base max-w-lg">Real-time sessions connecting invited investors directly with founders.</p>
      </div>
      <h2 className="text-xl font-semibold text-left mb-6 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-red-500 live-dot" /> Happening Now
      </h2>
      <div className="grid md:grid-cols-2 gap-8 mb-16" data-testid="live-now-grid">
        {live.map((w) => <WebinarCard key={w.id} w={w} />)}
      </div>
      <h2 className="text-xl font-semibold text-left mb-6">Upcoming Sessions</h2>
      <div className="grid md:grid-cols-2 gap-8" data-testid="upcoming-grid">
        {upcoming.map((w) => <WebinarCard key={w.id} w={w} />)}
      </div>
    </main>
  );
}
